import React, { useEffect } from 'react'
import { useLazyQuery, useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { checkOrderQuery, updateOrderQuery, refundOrderQuery } from '../../queries'
import { CheckOrder, CheckOrderVariables } from '../../queries/types/CheckOrder'
import { UpdateOrder, UpdateOrderVariables } from '../../queries/types/UpdateOrder'
import { RefundOrder, RefundOrderVariables } from '../../queries/types/RefundOrder'
import { toast } from 'react-toastify'
import { FindOrders_findOrders } from '../../queries/types/FindOrders'
import { OrderStatus } from '../../types/graphql-global-types'
import { ApolloStateButton } from '../button'
import { CallbackStatusBadge, ConfirmButton, DateTime, MethodBadge, MonoId, OrderStatusBadge } from '../ui'
import '../../utils'

interface OrderRowAttributes {
    order: FindOrders_findOrders;
}

export function OrderRow(props: OrderRowAttributes): JSX.Element {
    const { t } = useTranslation()
    const [ refundOrder, refundOrderResult ] = useMutation<RefundOrder, RefundOrderVariables>(refundOrderQuery)
    const [ checkOrder, checkOrderResult ] = useLazyQuery<CheckOrder, CheckOrderVariables>(checkOrderQuery, {
        fetchPolicy: 'network-only'
    })
    const [ updateOrder, updateOrderResult ] = useMutation<UpdateOrder, UpdateOrderVariables>(updateOrderQuery)
    const order = props.order

    function markOrderPaid(order: FindOrders_findOrders) {
        updateOrder({
            variables: {
                id: order.id,
                status: OrderStatus.paymentSuccess
            }
        })
    }

    function queryOrder(order: FindOrders_findOrders) {
        checkOrder({
            variables: {
                id: order.id
            }
        })
    }

    function refund(order: FindOrders_findOrders) {
        refundOrder({
            variables: {
                id: order.id
            }
        }).then((res) => {
            if (res.data?.refundOrder === true) {
                toast.success('Refund requested')
            } else {
                toast.error('Failed to refund')
            }
        }).catch(() => {/**/})
    }

    useEffect(() => {
        if (checkOrderResult.data) {
            if (checkOrderResult.data.checkOrder) {
                toast.success('Order status matched with provider')
            } else {
                toast.error('Order status mismatched from provider')
            }
        }
    }, [checkOrderResult])

    return (
        <tr className='border-b border-gray-100 last:border-0 hover:bg-gray-50'>
            <td className='px-4 py-3'><MonoId value={order.id} /></td>
            <td className='px-4 py-3 text-gray-800'>{order.product.title}</td>
            <td className='px-4 py-3'><MethodBadge method={order.paymentMethod} /></td>
            <td className='px-4 py-3 text-gray-800 whitespace-nowrap'>{order.amount.toCurrency(order.currency)}</td>
            <td className='px-4 py-3'><OrderStatusBadge status={order.status} /></td>
            <td className='px-4 py-3'><CallbackStatusBadge status={order.callbackStatus} /></td>
            <td className='px-4 py-3 text-sm'><DateTime value={order.createdAt} /></td>
            <td className='px-4 py-3'>
                <div className='flex flex-row items-center justify-end gap-2'>
                    {order.status != OrderStatus.paymentSuccess ? (
                        <ConfirmButton
                            variant='secondary'
                            confirmVariant='primary'
                            confirmLabel={t('orders.table.actions.markPaid')}
                            title={t('orders.confirm.markPaid.title')}
                            message={t('orders.confirm.markPaid.message')}
                            loading={updateOrderResult.loading}
                            onConfirm={() => markOrderPaid(order)}>
                            {t('orders.table.actions.markPaid')}
                        </ConfirmButton>
                    ) : null}
                    {order.status == OrderStatus.paymentSuccess ? (
                        <ConfirmButton
                            variant='danger'
                            confirmLabel={t('orders.table.actions.refund')}
                            title={t('orders.confirm.refund.title')}
                            message={t('orders.confirm.refund.message')}
                            loading={refundOrderResult.loading}
                            onConfirm={() => refund(order)}>
                            {t('orders.table.actions.refund')}
                        </ConfirmButton>
                    ) : null}
                    <ApolloStateButton size='sm' result={checkOrderResult} onClick={() => queryOrder(order)}>{t('orders.table.actions.check')}</ApolloStateButton>
                </div>
            </td>
        </tr>
    )
}
